import { PAGES_BASE } from './pages.api';

import type { WebsitePermission } from '../lib';
import type { WebsitePageType } from '@utazzo/shared';

/**
 * Configuration for the three WebsitePage editing surfaces that share the
 * PagesListPage / PageEditor / NewPageDialog components:
 *
 *  - Custom Pages   (pageType CUSTOM, `/admin/website/pages`, `web.pages.*`)
 *  - Tourism Boards (pageType TOURISM_BOARD, own controller + `web.tourism_boards.*`)
 *  - Landing Pages  (pageType LANDING_PAGE, own controller + `web.landing_pages.*`)
 *  - Best Time to Visit   (pageType BEST_TIME_TO_VISIT, own controller +
 *    `web.best_time_pages.*`)
 *  - Best Places to Visit (pageType BEST_PLACES_TO_VISIT, own controller +
 *    `web.best_places_pages.*`)
 *
 * `basePath` selects the type-scoped API controller; `editorBase` is the admin
 * route the editor is mounted at (so the correct sidebar item stays selected).
 * `caps` are the CMS capability checks that gate the write/publish/delete UI.
 */
/**
 * One tab in a fixed-tab editor (Destination-page style). Each tab edits a
 * single section of a known layout: the editor finds the section by
 * `sectionType` (creating it on demand) and renders its heading + content form.
 */
export interface FixedTab {
  /** Section type this tab edits (mirrors WebsiteSectionType). */
  sectionType: string;
  /** Tab label shown in the tab bar. */
  label: string;
  /**
   * Top-level group this tab sits under. Only meaningful when the screen also
   * declares `tabGroups`; ignored otherwise.
   */
  group?: string;
}

export interface PageScreenConfig {
  /** pageType stamped on newly-created pages from this screen. */
  createPageType: WebsitePageType;
  /** Server list filter. Omitted for Custom Pages (the API excludes typed rows). */
  listPageType?: WebsitePageType;
  basePath: string;
  editorBase: string;
  title: string;
  description: string;
  createLabel: string;
  createDialogTitle: string;
  createDialogDescription: string;
  createPlaceholder: string;
  emptyTitle: string;
  emptyDescription: string;
  caps: { write: WebsitePermission; publish: WebsitePermission; delete: WebsitePermission };
  /**
   * Capability required to touch the page's STRUCTURE - the Sections and SEO
   * tabs plus "Add section". When set, a user without it gets only the
   * content tabs (Position / Enquiries). Omit to always show every tab, which
   * is the behaviour for every surface whose audience owns the whole page.
   */
  structureCap?: WebsitePermission;
  /**
   * Where the editor's back arrow lands. Defaults to `editorBase` (the list
   * this page came from). Surfaces mounted at a fixed URL with no list behind
   * them - Careers - point somewhere useful instead.
   */
  backPath?: string;
  /**
   * Tailwind max-width for the editor body. Defaults to the narrow reading
   * column that suits a section composer; screens whose tabs are data tables
   * override it so the table is not boxed into the middle third of the page.
   */
  contentMaxWidthClass?: string;
  /**
   * When set, the editor renders a FIXED tabbed layout (like the Destination
   * page) instead of the free-form "Add a section" composer: one tab per entry
   * here, plus an SEO tab. Each tab edits a single section, created on demand.
   */
  fixedTabs?: FixedTab[];
  /**
   * When set alongside `fixedTabs`, the editor renders a TWO-LEVEL tab bar:
   * one top-level tab per entry here, each holding the `fixedTabs` whose
   * `group` matches, picked from a second row below. Screens with a dozen-plus
   * sections (the SEO guides) otherwise overflow the editor width. The SEO
   * panel always joins the LAST group. Omit for the short screens - they keep
   * the single flat tab strip.
   */
  tabGroups?: string[];
  /** Public URL path prefix for this page type (e.g. "/tourism"). The public
   *  slug shown in the editor is `${publicPathPrefix}/<slug>`. */
  publicPathPrefix?: string;
}

export const CUSTOM_PAGES_CONFIG: PageScreenConfig = {
  createPageType: 'CUSTOM',
  basePath: PAGES_BASE,
  editorBase: '/pages',
  title: 'Custom Pages',
  description: 'The pages that make up the public website. Edit sections, preview, and publish.',
  createLabel: 'New page',
  createDialogTitle: 'New custom page',
  createDialogDescription: 'Give the page a title. You can compose its sections and SEO next.',
  createPlaceholder: 'e.g. Summer Europe Offers',
  emptyTitle: 'No pages yet',
  emptyDescription: 'Create your first page to start composing the website.',
  caps: { write: 'pages.write', publish: 'pages.publish', delete: 'pages.delete' },
};

/**
 * Careers screen. Same CUSTOM WebsitePage row and same `/admin/website/pages`
 * controller as Custom Pages - only the capability gates differ, so a role
 * granted `web.career.*` (the HR desk) can edit Careers without holding
 * `web.pages.*`. `editorBase` stays `/career` so the sidebar item keeps its
 * selection. See CareerEditor.
 */
export const CAREER_PAGE_CONFIG: PageScreenConfig = {
  ...CUSTOM_PAGES_CONFIG,
  editorBase: '/career',
  title: 'Career',
  description: 'Careers page: open positions, application form, and page sections.',
  caps: { write: 'career.write', publish: 'career.publish', delete: 'career.delete' },
  // The HR desk owns the job postings and the applicant queue, not the page
  // layout or its SEO - those stay with the website team. `pages.write` is the
  // divider: HR holds web.career.write only, so it sees Position + Enquiries;
  // anyone holding web.pages.write keeps the full Sections + SEO editor.
  structureCap: 'pages.write',
  // There is no Careers list - the sidebar item opens this editor directly, so
  // the back arrow returned to the page it was already on. Send it home.
  backPath: '/',
  // Position + Enquiries are wide data tables; the default reading column left
  // most of the screen empty and wrapped every row.
  contentMaxWidthClass: 'max-w-7xl',
};

export const TOURISM_BOARDS_CONFIG: PageScreenConfig = {
  createPageType: 'TOURISM_BOARD',
  listPageType: 'TOURISM_BOARD',
  basePath: '/admin/website/tourism-boards',
  editorBase: '/tourism-boards',
  title: 'Tourism Boards',
  description: 'Country / region tourism-board landing pages. Compose sections, preview, publish.',
  createLabel: 'New tourism board',
  createDialogTitle: 'New tourism board',
  createDialogDescription: 'Give the tourism board a title. You can compose its sections next.',
  createPlaceholder: 'e.g. Discover Europe',
  emptyTitle: 'No tourism boards yet',
  emptyDescription: 'Create your first tourism-board page to start composing it.',
  caps: {
    write: 'tourism_boards.write',
    publish: 'tourism_boards.publish',
    delete: 'tourism_boards.delete',
  },
  // Tourism boards are served at /tourism/<slug> on the website.
  publicPathPrefix: '/tourism',
  // Fixed tabbed editor (Discover Europe layout), like the Destination page.
  // One tab per section; the editor creates the section on first edit.
  fixedTabs: [
    { sectionType: 'HERO', label: 'Hero' },
    { sectionType: 'QUICK_INFO', label: 'Quick info' },
    { sectionType: 'RICH_TEXT', label: 'About' },
    { sectionType: 'BEST_TIME', label: 'Best time' },
    { sectionType: 'CALLBACK_CHAT', label: 'Callback' },
    { sectionType: 'PLACES_GRID', label: 'Places' },
    { sectionType: 'PACKAGE_CAROUSELS', label: 'Packages' },
    { sectionType: 'EXPLORE_THEMES', label: 'Things to do' },
    { sectionType: 'EVENTS_GRID', label: 'Events' },
    { sectionType: 'ACTIVITIES_GRID', label: 'Your people' },
    { sectionType: 'CLIENT_STORY_CARDS', label: 'Reviews' },
    { sectionType: 'FAQ_LIST', label: 'FAQ' },
  ],
};

export const LANDING_PAGES_CONFIG: PageScreenConfig = {
  createPageType: 'LANDING_PAGE',
  listPageType: 'LANDING_PAGE',
  basePath: '/admin/website/landing-pages',
  editorBase: '/landing-pages',
  title: 'Landing Pages',
  description: 'Marketing / campaign landing pages. Compose sections, preview, publish.',
  createLabel: 'New landing page',
  createDialogTitle: 'New landing page',
  createDialogDescription: 'Give the landing page a title. You can compose its sections next.',
  createPlaceholder: 'e.g. Summer Sale 2026',
  emptyTitle: 'No landing pages yet',
  emptyDescription: 'Create your first landing page to start composing it.',
  caps: {
    write: 'landing_pages.write',
    publish: 'landing_pages.publish',
    delete: 'landing_pages.delete',
  },
  // Landing pages are served at /lp/<slug> on the website.
  publicPathPrefix: '/lp',
  // Fixed tabbed editor (Stress-Free Europe layout), like Tourism Boards.
  // Callback sits right after Packages so the website renders it as the sticky
  // right-rail form beside the package cards; the same section renders again
  // as the full-width "Request a callback" strip below the FAQ (like tourism).
  fixedTabs: [
    { sectionType: 'HERO', label: 'Hero' },
    { sectionType: 'LANDING_PACKAGES', label: 'Packages' },
    { sectionType: 'CALLBACK_CHAT', label: 'Callback' },
    { sectionType: 'CLIENT_STORY_CARDS', label: 'Reviews' },
    { sectionType: 'FAQ_LIST', label: 'FAQ' },
  ],
};

/**
 * "Best time to visit <Destination>" - the first of the two destination SEO
 * guides. Fixed-tab editor like Tourism Boards; every tab maps to a section
 * type that already has an editor form in `section-forms.tsx`, so nothing here
 * renders the "No editor for X yet" placeholder.
 */
export const BEST_TIME_PAGES_CONFIG: PageScreenConfig = {
  createPageType: 'BEST_TIME_TO_VISIT',
  listPageType: 'BEST_TIME_TO_VISIT',
  basePath: '/admin/website/best-time-pages',
  editorBase: '/best-time-to-visit',
  title: 'Best Time to Visit',
  description:
    'Destination "Best time to visit" SEO guides - weather, month-by-month, festivals. Compose sections, preview, publish.',
  createLabel: 'New guide',
  createDialogTitle: 'New "Best time to visit" guide',
  createDialogDescription: 'Give the guide a title. You can compose its sections next.',
  createPlaceholder: 'e.g. Best Time to Visit Bali',
  emptyTitle: 'No "Best time to visit" guides yet',
  emptyDescription: 'Create your first guide to start composing it.',
  caps: {
    write: 'best_time_pages.write',
    publish: 'best_time_pages.publish',
    delete: 'best_time_pages.delete',
  },
  // Served at the ROOT - /<slug> - so the author owns the whole URL. The old
  // /best-time-to-visit/<slug> path permanently redirects here.
  publicPathPrefix: '',
  // 13 sections + SEO never fit one strip, so they are grouped: what the guide
  // says, what it sells, and what converts. Tabs stay in page order within
  // each group.
  tabGroups: ['Content', 'Packages', 'Conversion', 'SEO'],
  fixedTabs: [
    { sectionType: 'HERO', label: 'Hero', group: 'Content' },
    { sectionType: 'RICH_TEXT', label: 'Overview', group: 'Content' },
    { sectionType: 'STATS_STRIP', label: 'Weather snapshot', group: 'Content' },
    { sectionType: 'BEST_TIME', label: 'Monthly guide', group: 'Content' },
    { sectionType: 'MONTH_BY_MONTH', label: 'Month by month', group: 'Content' },
    { sectionType: 'TRAVEL_STYLES', label: 'Travel styles', group: 'Content' },
    { sectionType: 'EVENTS_GRID', label: 'Festivals', group: 'Content' },
    { sectionType: 'FEATURE_LIST', label: 'Packing guide', group: 'Content' },
    // The guides show hand-picked cards for one destination rather than the
    // theme-tagged carousels the tourism / landing pages use.
    { sectionType: 'RECOMMENDED_PACKAGES', label: 'Packages', group: 'Packages' },
    // Pinned in the page's right rail (above the lead form), not in the flow.
    { sectionType: 'CTA_BANNER', label: 'Packages promo', group: 'Packages' },
    { sectionType: 'CALLBACK_CHAT', label: 'Callback', group: 'Conversion' },
    { sectionType: 'FAQ_LIST', label: 'FAQ', group: 'Conversion' },
    { sectionType: 'FEATURE_CARDS', label: 'Related guides', group: 'Conversion' },
  ],
};

/**
 * "Best places to visit in <Destination>" - the attractions guide. Shorter
 * than its sibling: the body is one places grid plus the shared package /
 * callback / FAQ rail.
 */
export const BEST_PLACES_PAGES_CONFIG: PageScreenConfig = {
  createPageType: 'BEST_PLACES_TO_VISIT',
  listPageType: 'BEST_PLACES_TO_VISIT',
  basePath: '/admin/website/best-places-pages',
  editorBase: '/best-places-to-visit',
  title: 'Best Places to Visit',
  description:
    'Destination "Best places to visit" SEO guides - attractions, timings, entry fees. Compose sections, preview, publish.',
  createLabel: 'New guide',
  createDialogTitle: 'New "Best places to visit" guide',
  createDialogDescription: 'Give the guide a title. You can compose its sections next.',
  createPlaceholder: 'e.g. Best Places to Visit in Bali',
  emptyTitle: 'No "Best places to visit" guides yet',
  emptyDescription: 'Create your first guide to start composing it.',
  caps: {
    write: 'best_places_pages.write',
    publish: 'best_places_pages.publish',
    delete: 'best_places_pages.delete',
  },
  // Served at the ROOT - /<slug> - so the author owns the whole URL. The old
  // /best-places-to-visit/<slug> path permanently redirects here.
  publicPathPrefix: '',
  fixedTabs: [
    { sectionType: 'HERO', label: 'Hero' },
    { sectionType: 'RICH_TEXT', label: 'Overview' },
    // Figma: first place cards, then packages, then remaining place cards.
    { sectionType: 'PLACES_GRID', label: 'Places' },
    { sectionType: 'RECOMMENDED_PACKAGES', label: 'Packages' },
    // Pinned in the page's right rail (above the lead form), not in the flow.
    { sectionType: 'CTA_BANNER', label: 'Packages promo' },
    { sectionType: 'CALLBACK_CHAT', label: 'Callback' },
    { sectionType: 'FAQ_LIST', label: 'FAQ' },
    { sectionType: 'FEATURE_CARDS', label: 'Explore more guides' },
  ],
};
